import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Box, Button, Grid, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  useOperatorsUserMessagingGroupsMembersQuery,
  useOperatorsUserMessagingUsersQuery,
  usePostOperatorsUserMessagingGroupsMembersMutation,
} from 'api/queries/operators';
import { CustomPagination, MessageGroupMember, PageLoader, colors } from 'remote/global';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DataGridOverlay } from 'utils/component-utils';
import { MemberListSchemaType, memberListSchema } from './types';

interface Props {
  onClose: () => void;
}

function AddMembersToGroupForm({ onClose }: Props) {
  const [selectedMember, setSelectedMember] = useState<MessageGroupMember | null>(null);
  const { id } = useParams();
  const [pageNo, setPageNo] = useState('0');
  const { data: messageGroupMembers } = useOperatorsUserMessagingGroupsMembersQuery(id!);
  const { data: messageGroupUsers } = useOperatorsUserMessagingUsersQuery({
    pageNo,
    namePart: selectedMember ? selectedMember.contactInfo.lastName : '',
  });
  const { t } = useTranslation();
  const postOperatorsUserMessagingGroupsMembersMutation =
    usePostOperatorsUserMessagingGroupsMembersMutation(id!);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<MemberListSchemaType>({
    defaultValues: {
      members: [],
    },
    resolver: zodResolver(memberListSchema),
  });
  const membersWatch = watch('members');

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('MessagesView.group.current.table.header.name').toString(),
        flex: 2,
        renderCell: ({ row }) => {
          return (
            <Box>
              <Typography
                sx={{
                  color: colors.gray2,
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                {row.contactInfo.firstName} {row.contactInfo.lastName}
              </Typography>
              <Typography
                sx={{
                  color: colors.gray1,
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                {row.contactInfo.email}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [t]
  );

  const onValid = async (data: MemberListSchemaType) => {
    await postOperatorsUserMessagingGroupsMembersMutation.mutateAsync(data.members);
    setPageNo('0');
    onClose();
  };

  const idsToFilter =
    messageGroupMembers?.elements?.map((member: MessageGroupMember) => member.userId) ?? [];

  const filteredUsers =
    messageGroupUsers?.elements?.filter(
      (user: MessageGroupMember) => !idsToFilter.includes(user.userId)
    ) ?? [];

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid item container spacing={2} display='flex'>
        <Grid item xs={12}>
          <Autocomplete
            sx={{
              mb: '6px',
              maxWidth: '343px',
            }}
            fullWidth
            disablePortal
            id='message-group-member'
            value={selectedMember}
            onChange={(_, newValue) => {
              setSelectedMember(newValue);
            }}
            getOptionLabel={(option: MessageGroupMember) =>
              `${option.contactInfo.firstName} ${option.contactInfo.lastName}` || ''
            }
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            options={filteredUsers}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.userId}>
                  {`${option.contactInfo.firstName} ${option.contactInfo.lastName}` || ''}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                placeholder={t('FormFields.search.label').toString()}
                {...params}
                label=''
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={filteredUsers}
            getRowId={(row) => row.userId}
            columns={columns}
            paginationModel={{
              page: parseInt(pageNo, 10),
              pageSize: 5,
            }}
            hideFooterSelectedRowCount
            columnHeaderHeight={52}
            onRowSelectionModelChange={(newSelection) => {
              setValue('members', newSelection as number[]);
            }}
            rowSelectionModel={membersWatch}
            checkboxSelection
            disableColumnSelector
            disableColumnMenu
            disableRowSelectionOnClick
            pagination
            slots={{
              noRowsOverlay: () => DataGridOverlay(t('Placeholder.noRows')),
              noResultsOverlay: () => DataGridOverlay(t('Placeholder.noResults')),
              loadingOverlay: PageLoader,
              pagination: (props) =>
                CustomPagination({
                  ...props,
                  t,
                  pageCount: Math.ceil(filteredUsers.length / 5),
                  pageNo: Number(pageNo),
                  onChange: (_, page) => {
                    setPageNo(String(page - 1));
                  },
                }),
            }}
          />
        </Grid>
        <Grid item xs={12} textAlign='right'>
          <Button
            type='submit'
            variant='contained'
            disabled={isSubmitting || isValidating || isLoading}
            sx={{ mt: '2rem' }}
          >
            {t('confirm')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default AddMembersToGroupForm;
